import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { AndroWebCoreComponent } from '@app/core/AndroWebCoreComponent';
import { NavigatorService } from '@app/core/navigator.service';
import { CustomHtmlSection } from '@app/models/custom-html-section';
import { NavLink } from '@app/models/shared/nav-link';
import { Site } from '@app/models/site';
import { PaymentProviderType } from '@app/models/site/PaymentProviderType.enum';
import { SiteService } from '@app/api/site.service';
import { takeUntil } from 'rxjs';
import { SocialMediaAccountType } from '@app/models/social-media-account-type';
import { SocialMediaLinks } from '@app/models/social-media-links';
import { SocialMediaAccount } from '@app/models/social-media-account';

@Component({
  imports: [CommonModule, RouterModule],
  selector: 'app-footer',
  standalone: true,
  styleUrls: ['./footer.component.scss'],
  templateUrl: './footer.component.html'
})

export class FooterComponent extends AndroWebCoreComponent implements OnInit {
  public navLinks: NavLink[];
  public showStripeBanner: boolean;
  public hasPageSeoKeywords: boolean;
  public socialMediaLinks: SocialMediaLinks[];
  public appStoreUrl: string;
  public playStoreUrl: string;

  private _showLocationsUrl: boolean;

  constructor(
    private _siteService: SiteService,
    private _navigatorService: NavigatorService
  ) {
    super();
  }

  ngOnInit() {
    this.hasPageSeoKeywords = this.tenant?.PageSeos[0]?.Keywords?.length > 0;

    this._siteService.getSites()
        .pipe(takeUntil(this.destroy$))
        .subscribe((sites: Site[]) => {
          this.showStripeBanner = sites.some((x: Site) => x.PaymentProvidersSupported.includes(PaymentProviderType.Stripe));
          this._showLocationsUrl = sites.length > 0;
          this.setNavLinks();
        });

    this.setSocialMediaLinks();
    this.appStoreUrl = this.tenant?.AppStoreUrl;
    this.playStoreUrl = this.tenant?.PlayStoreUrl;
  }

  /**
  * opens https://andromedapos.com/ in a new window
  */
  public navigateToAndromeda(): void {
    window.open('https://andromedapos.com/');
  }

  /**
   * sets the navigation links.
   * @param sites
   */
  private setNavLinks(): void {
    const navLinks: NavLink[] = [];

    if (this.tenant.CorporateLinkUri) {
      navLinks.push({ isExternal: true, label: 'Home Page', routerLink: this.tenant.CorporateLinkUri });
    }

    if (this._showLocationsUrl) {
      navLinks.push({ label: 'Locations', routerLink: '/locations' });
    }

    this.tenant.CustomHtmlSections
        .filter((x: CustomHtmlSection) => x.Enabled && x.DisplayOrder !== 0 && x.Title.toLowerCase() !== 'follow us on social media')
        .map((x: CustomHtmlSection) => x.Title)
        .forEach((title: string) => {
          navLinks.push({
            label: title,
            routerLink: `/sections/${this._navigatorService.clean(title)}`
          });
        });

    this.navLinks = navLinks;
  }

  private setSocialMediaLinks(): void {
    this.socialMediaLinks = this.tenant.SocialMediaAccounts.sort((first: SocialMediaAccount, second: SocialMediaAccount) => first.Type - second.Type).map((x) => ({
      icon: this.getSocialMediaIcon(x.Type),
      url: x.Url,
      class: SocialMediaAccountType[x.Type].toLowerCase()
    } as SocialMediaLinks));
  }

  private getSocialMediaIcon(type: SocialMediaAccountType): string {
    switch (type) {
      case SocialMediaAccountType.Facebook: return 'facebook';
      case SocialMediaAccountType.Instagram: return 'instagram';
      case SocialMediaAccountType.TikTok: return 'tiktok';
      case SocialMediaAccountType.X: return 'x';
      case SocialMediaAccountType.YouTube: return 'youtube';
      case SocialMediaAccountType.WhatsApp: return 'whatsapp';
      case SocialMediaAccountType.Snapchat: return 'snapchat';
      case SocialMediaAccountType.Pinterest: return 'pinterest';
      default: return null;
    }
  }
}
